import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { ResponsiveContainer, ResponsiveHeader, ResponsiveContent, ResponsiveFooter } from '../ui/ResponsiveLayout';

interface FinancialArrangements {
  petCareBudget: string;
  petCareInstructions: string;
  dependentCareBudget: string;
  dependentCareInstructions: string;
  trustFunds: string;
  insurancePolicies: string;
  emergencyFunds: string;
  specialInstructions: string;
  paymentSchedule: string;
  careCoordinator: string;
  careCoordinatorContact: string;
}

const Frame243: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();
  
  const [arrangements, setArrangements] = useState<FinancialArrangements>({
    petCareBudget: '',
    petCareInstructions: '',
    dependentCareBudget: '',
    dependentCareInstructions: '',
    trustFunds: '',
    insurancePolicies: '',
    emergencyFunds: '',
    specialInstructions: '',
    paymentSchedule: '',
    careCoordinator: '',
    careCoordinatorContact: ''
  });

  // Helper function to save data directly to localStorage
  const saveToLocalStorage = (arrangementsToSave: FinancialArrangements) => {
    try {
      const dataToSave = {
        answers: {
          financialArrangements: arrangementsToSave
        },
        lastUpdated: new Date().toISOString()
      };
      
      // Save to backup location for redundancy
      localStorage.setItem('pets-financial-backup', JSON.stringify(arrangementsToSave));
      localStorage.setItem('pets-financial-data-backup', JSON.stringify(dataToSave));
      
      return true;
    } catch (error) {
      console.error('[Frame243] Direct save failed:', error);
      return false;
    }
  };

  // Load saved data on component mount - PRIORITIZE SESSION FOR CROSS-DEVICE SYNC
  useEffect(() => {
    // Priority 1: Load from session (authoritative source for cross-device sync)
    if (session?.taskProgress?.pets?.answers) {
      const savedData = session.taskProgress.pets.answers.financialArrangements;
      if (savedData) {
        console.log('[Frame243] Loading from session:', savedData);
        setArrangements(savedData);
        return; // Session data takes priority
      }
    }

    // Priority 2: Fall back to localStorage (for same-device persistence)
    try {
      const backupData = localStorage.getItem('pets-financial-backup');
      if (backupData) {
        const data = JSON.parse(backupData);
        if (data) {
          console.log('[Frame243] Loading from localStorage backup:', data);
          setArrangements(data);
        }
      }
    } catch (error) {
      console.error('[Frame243] Failed to load from backup:', error);
    }
  }, [session?.taskProgress?.pets]);

  const handleInputChange = (field: keyof FinancialArrangements, value: string) => {
    const newArrangements = {
      ...arrangements,
      [field]: value
    };
    
    setArrangements(newArrangements);
    
    // IMMEDIATE DIRECT SAVE
    saveToLocalStorage(newArrangements);

    // Also save to session (but don't wait for it)
    updateTaskProgress('pets', {
      financialArrangements: newArrangements
    }).catch(error => {
      console.error('[Frame243] Session save failed:', error);
    });
  };

  const handleNext = async () => {
    try {
      // IMMEDIATE DIRECT SAVE
      if (!saveToLocalStorage(arrangements)) {
        throw new Error('Failed to save to local storage');
      }

      // Save to session and wait for it to complete
      await updateTaskProgress('pets', {
        financialArrangements: arrangements
      });
      
      // Only navigate if both saves were successful
      navigate('/frame-244-pets');
    } catch (error) {
      console.error('[Frame243] Save failed:', error);
      
      // Show error to user
      const message = 'Failed to save your changes. Please try again. If the problem persists, your session may have expired - try refreshing the page.';
      if (confirm(message + '\n\nClick OK to try again or Cancel to discard changes.')) {
        // Try again
        handleNext();
      } else {
        // User chose to discard changes
        navigate('/frame-244-pets');
      }
    }
  };

const handleBack = async () => {
    try {
      // IMMEDIATE DIRECT SAVE
      if (!saveToLocalStorage(arrangements)) {
        throw new Error('Failed to save to local storage');
      }

      // Save to session and wait for it to complete
      await updateTaskProgress('pets', {
        financialArrangements: arrangements
      });
      
      // CHANGE: Navigate to the unique frame number instead of using ?section=
      navigate('/frame-249'); 
      // Navigate back to the Dependents page (Frame249)
      navigate('/frame-249');
    } catch (error) {
      console.error('[Frame243] Save failed:', error);
      
      const message = 'Failed to save your changes. Please try again. If the problem persists, your session may have expired - try refreshing the page.';
      if (confirm(message + '\n\nClick OK to try again or Cancel to discard changes.')) {
        handleBack();
      } else {

        // CHANGE: Navigate to the unique frame number here as well
        // Even if user discards, we still want them back on the Dependents page
        navigate('/frame-249');
      }
    }
  };

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader
          title="Financial Arrangements for Care"
        />
        
        <div className="max-w-4xl">
          <div className="text-stone-800 text-lg font-medium font-sans mb-8">
            Establish financial arrangements to ensure proper care for your pets and dependants
          </div>
          
          <div className="space-y-8">
            {/* Pet Care Budget */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-4">
                Pet Care Budget
              </label>
              <input
                type="text"
                value={arrangements.petCareBudget}
                onChange={(e) => handleInputChange('petCareBudget', e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700"
                placeholder="Enter monthly/annual budget for pet care"
              />
            </div>

            {/* Pet Care Financial Instructions */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-4">
                Pet Care Financial Instructions
              </label>
              <textarea
                value={arrangements.petCareInstructions}
                onChange={(e) => handleInputChange('petCareInstructions', e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700"
                placeholder="How should pet care expenses be handled? Include payment instructions, account details, etc."
                rows={4}
              />
            </div>

            {/* Dependant Care Budget */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-4">
                Dependent Care Budget
              </label>
              <input
                type="text"
                value={arrangements.dependentCareBudget}
                onChange={(e) => handleInputChange('dependentCareBudget', e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700"
                placeholder="Enter monthly/annual budget for dependent care"
              />
            </div>

            {/* Dependent Care Financial Instructions */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-4">
                Dependent Care Financial Instructions
              </label>
              <textarea
                value={arrangements.dependentCareInstructions}
                onChange={(e) => handleInputChange('dependentCareInstructions', e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700"
                placeholder="How should dependent care expenses be handled? Include payment instructions, account details, etc."
                rows={4}
              />
            </div>

            {/* Trust Funds */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-4">
                Trust Funds or Special Accounts
              </label>
              <textarea
                value={arrangements.trustFunds}
                onChange={(e) => handleInputChange('trustFunds', e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700"
                placeholder="Describe any trust funds or special accounts set up for pet or dependent care"
                rows={3}
              />
            </div>

            {/* Insurance Policies */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-4">
                Insurance Policies
              </label>
              <textarea
                value={arrangements.insurancePolicies}
                onChange={(e) => handleInputChange('insurancePolicies', e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700"
                placeholder="List any insurance policies that cover pet or dependent care"
                rows={3}
              />
            </div>

            {/* Emergency Funds */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-4">
                Emergency Funds
              </label>
              <textarea
                value={arrangements.emergencyFunds}
                onChange={(e) => handleInputChange('emergencyFunds', e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700"
                placeholder="Describe emergency funds or procedures for unexpected care expenses"
                rows={3}
              />
            </div>

            {/* Care Coordinator */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-stone-800 text-base font-medium font-sans mb-4">
                  Care Coordinator Name
                </label>
                <input
                  type="text"
                  value={arrangements.careCoordinator}
                  onChange={(e) => handleInputChange('careCoordinator', e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700"
                  placeholder="Person responsible for coordinating care and finances"
                />
              </div>
              
              <div>
                <label className="block text-stone-800 text-base font-medium font-sans mb-4">
                  Care Coordinator Contact
                </label>
                <input
                  type="text"
                  value={arrangements.careCoordinatorContact}
                  onChange={(e) => handleInputChange('careCoordinatorContact', e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700"
                  placeholder="Phone number or email"
                />
              </div>
            </div>

            {/* Payment Schedule */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-4">
                Payment Schedule
              </label>
              <textarea
                value={arrangements.paymentSchedule}
                onChange={(e) => handleInputChange('paymentSchedule', e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700"
                placeholder="How often should payments be made? Who should receive them? Any timing considerations?"
                rows={3}
              />
            </div>

            {/* Special Instructions */}
            <div>
              <label className="block text-stone-800 text-base font-medium font-sans mb-4">
                Special Financial Instructions
              </label>
              <textarea
                value={arrangements.specialInstructions}
                onChange={(e) => handleInputChange('specialInstructions', e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-700"
                placeholder="Any special instructions or considerations for managing the financial aspects of care"
                rows={4}
              />
            </div>
          </div>
        </div>
      </ResponsiveContent>
      
      <ResponsiveFooter
        title="Plan for Your Pets and Dependents"
        subtitle="Document financial arrangements for care"
        progressPercent={60}
        onBack={handleBack}
        onNext={handleNext}
        nextText="Next"
        backText="Back"
      />
    </ResponsiveContainer>
  );
};

export default Frame243; 